'use client';

import React from 'react';
import Link from '@ui/components/Link/Link';
import type { CampaignFragment, Maybe } from '@business/gql/graphql';
import { Product } from '@ts/product';
import { Heading } from '@ui/components/Heading/Heading';
import Paragraph from '@ui/components/Paragraph/Paragraph';
import { ContentfulImage } from '@ui/components/Contentful';

export const campaignIsActive = (campaign: CampaignFragment) => {
  const currentDate = new Date();
  const campaignStart = new Date(campaign.campaignStart);
  const campaignEnd = new Date(campaign.campaignEnd);
  return (
    campaignStart.getTime() <= currentDate.getTime() &&
    currentDate.getTime() <= campaignEnd.getTime()
  );
};

const shouldShowCampaignBadge = (
  priceType: string,
  campaign: CampaignFragment,
): boolean => {
  const isActive = campaignIsActive(campaign);
  if (!isActive) return false;
  if (campaign.showBadgeForAllProductsInCampaign) {
    return true;
  }
  const isCampaign = priceType === 'campaign';
  if (!isCampaign) return false;
  return true;
};

const CampaignBanner = ({
  cta,
  product,
  campaign,
}: {
  cta?: string;
  product: Product;
  campaign?: Maybe<CampaignFragment>;
}) => {
  if (!campaign) return null;
  if (!shouldShowCampaignBadge(product.priceType, campaign)) return null;
  if (!campaign.campaignLabel || !campaign.slug) return null;

  return (
    <section className="mt-4 grid grid-cols-[176px,auto] grid-rows-[176px,auto] bg-beige">
      <div className="relative h-full w-full max-lg:col-span-2 lg:row-span-2">
        <ContentfulImage
          desktopImage={campaign.campaignImage}
          mobileImage={campaign.pageImage}
          sizes="(max-width: 1024px) 750px, 375px"
          className="h-full w-full object-cover object-center"
          breakpoint="lg"
        />
      </div>
      <div className="flex flex-col items-start justify-evenly gap-y-4 px-4 py-4 max-lg:col-span-2 lg:row-span-2 lg:px-6">
        <Heading as="h3" className="font-bold">
          {campaign.campaignLabel}
        </Heading>
        <Paragraph className="prose text-xs">
          {campaign.shortDescription}
        </Paragraph>
        <Link
          href={campaign.slug}
          className="bg-red p-2 font-serif text-xxs text-white">
          {cta}
        </Link>
      </div>
    </section>
  );
};

export default CampaignBanner;
